import DeleteForeverIcon from '@mui/icons-material/DeleteForever'
import LabelIcon from '@mui/icons-material/Label'
import {
  Box,
  Card,
  CardHeader,
  Container,
  IconButton,
  Typography,
  useMediaQuery,
} from '@mui/material'
import get from 'lodash/get'
import React, { useContext, useState } from 'react'
import { useSelector } from 'react-redux'
import { useActions } from '../hooks/useActions'
import * as CancersList from '../utils/CancerTypes'
import Context from '../utils/context/Context'
import { pathToPerson } from '../utils/helpers'
import DialogWindow from './DialogWindow'

function CancerDetails({ treatments, qualifiers }) {
  return (
    <Box display="flex" flexDirection="column" gap={0.5} pt={0.5}>
      {qualifiers.map(({ numericAgeAtDiagnosis, laterality, id: qualifierId }) => (
        <Typography key={qualifierId + numericAgeAtDiagnosis + laterality} variant="subtitle2">
          Diagnosis Age: <b>{ageText(numericAgeAtDiagnosis, laterality)}</b>
        </Typography>
      ))}
      {treatments.map(({ treatmentLocation, patientAddress }) => [
        treatmentLocation ? (
          <Typography key={'ctl-' + treatmentLocation} variant="subtitle2">
            Treatment Location: <b>{treatmentLocation}</b>
          </Typography>
        ) : null,
        patientAddress ? (
          <Typography key={'cpa-' + patientAddress} variant="subtitle2">
            Home Address: <b>{patientAddress}</b>
          </Typography>
        ) : null,
      ])}
    </Box>
  )

  function ageText(age, laterality) {
    let ageText = ''
    if (age === undefined || age > Number.MAX_VALUE) {
      ageText = 'unknown age'
    } else {
      ageText = age
    }
    if (laterality) {
      ageText += ` (${laterality})`
    }

    return ageText
  }
}

export default function CancerList({ personId, editCancer }) {
  const [modalConfirmationOpened, setModalConfirmationOpened] = useState(false)
  const [deleteCancerId, setDeleteCancerId] = useState('')

  const { setLoading } = useContext(Context)
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down('md'))
  const cancers = useSelector((state) => get(state, [...pathToPerson(personId), 'cancers'])) || []
  const mantleRadiation =
    useSelector((state) => {
      const treatments = get(state, [...pathToPerson(personId), 'treatments']) || []

      return treatments.find(({ type }) => type === 'mantleRadiation')
    }) || {}
  const { removePersonCancer, removePersonTreatments } = useActions()

  return cancers?.length > 0 ? (
    <>
      <Container>
        {cancers.map((cancer) => {
          const { id: cancerId, label = '', qualifiers = [], treatments = [] } = cancer

          return (
            <Card
              key={cancerId}
              elevation={2}
              sx={{ borderRadius: 2, cursor: 'pointer' }}
              onClick={() => {
                editCancer(cancer)
              }}
            >
              <CardHeader
                sx={{
                  backgroundColor: (theme) => theme.palette.white.main,
                  py: 2,
                  px: isMobile ? 2 : 3,
                }}
                avatar={<LabelIcon color="primary" />}
                title={CancersList.findCancerLabel(cancerId) || label}
                subheader={<CancerDetails treatments={treatments} qualifiers={qualifiers} />}
                action={
                  <IconButton
                    color="error"
                    onClick={(e) => {
                      e.stopPropagation()
                      setModalConfirmationOpened(true)
                      setDeleteCancerId(cancerId)
                    }}
                  >
                    <DeleteForeverIcon />
                  </IconButton>
                }
              />
            </Card>
          )
        })}
      </Container>
      <DialogWindow
        open={modalConfirmationOpened}
        onClose={() => {
          setModalConfirmationOpened(false)
          setLoading((prev) => ({ ...prev, dialog: false }))
        }}
        handleConfirm={handleDeleteCancer}
        title="Remove Cancer"
        text="Are you sure you want to remove this item?"
        color="warning"
      />
    </>
  ) : null

  function handleDeleteCancer() {
    setModalConfirmationOpened(false)
    // clean up mantle radiation treatment
    if (deleteCancerId === 'HP:0012189' && mantleRadiation) {
      removePersonTreatments({ id: personId, type: 'mantleRadiation' })
    }
    removePersonCancer({ id: personId, cancerId: deleteCancerId })
    setLoading((prev) => ({ ...prev, dialog: false }))
  }
}
